<script setup>
import moment from 'moment'
import { computed, ref } from 'vue'
import { getFormColor } from '../../../composables/useReservationsCalendar.js'
import { trans } from 'laravel-vue-i18n'
import Popover from 'primevue/popover'

const DAY_NAMES = [
    trans('common.dow_short.mon'),
    trans('common.dow_short.tue'),
    trans('common.dow_short.wed'),
    trans('common.dow_short.thu'),
    trans('common.dow_short.fri'),
    trans('common.dow_short.sat'),
    trans('common.dow_short.sun'),
]
const MAX_MULTIDAY_TRACKS = 4 // po tomhle počtu tracků se zobrazí "+N"
const ROW_HEIGHT = 17 // px — musí sedět s výškou .multiday-track, aby do stejné mřížky zapadly i jednodenní rezervace

const emit = defineEmits(['captureReservationChange', 'toggleEvent', 'hoverReservation'])

const props = defineProps({
    days:          { type: Array, default: () => [] },
    selectedMonth: { type: [Object, Date] },
})

// Flat pole → týdny
const weeks = computed(() => {
    const result = []
    for (let i = 0; i < props.days.length; i += 7) {
        result.push(props.days.slice(i, i + 7))
    }
    return result
})

// ─── Barvy ───────────────────────────────────────────────────────

function resColor(reservation) {
    return getFormColor(reservation.reservation_forms)
}

// Hex barva s alpha suffixem (pro pending bg)
function colorWithAlpha(hex, alpha) {
    return hex + Math.round(alpha * 255).toString(16).padStart(2, '0')
}

function multidayPillStyle(reservation) {
    const color = resColor(reservation)
    if (reservation.state === 'rejected') {
        return { background: 'transparent', color: '#9BA1AB', border: '1px dashed #C9CDD4', textDecoration: 'line-through' }
    }
    if (reservation.state === 'pending') {
        return { background: colorWithAlpha(color, 0.12), color, border: `1px dashed ${color}` }
    }
    return { background: color, color: '#fff', border: 'none' }
}

function singleDayChipStyle(reservation) {
    const color = resColor(reservation)
    if (reservation.state === 'rejected') {
        return { background: 'transparent', color: '#9BA1AB', textDecoration: 'line-through' }
    }
    if (reservation.state === 'pending') {
        return { background: colorWithAlpha(color, 0.12), color }
    }
    return { background: color, color: '#fff' }
}

// ─── Multi-day tracks ────────────────────────────────────────────

function multidayTracksForWeek(week) {
    const seen = new Set()
    const events = []

    week.forEach((day, dayIndex) => {
        if (!day) return
        day.reservations
            .filter(r => r.date_start && r.date_end && !seen.has(r.id))
            .forEach(r => {
                seen.add(r.id)
                let endCol = dayIndex + 1
                for (let j = dayIndex + 1; j < week.length; j++) {
                    const next = week[j]
                    if (next && moment(next.date).isSameOrBefore(r.date_end, 'day')) {
                        endCol = j + 1
                    } else break
                }
                events.push({ reservation: r, startCol: dayIndex + 1, span: endCol - dayIndex })
            })
    })

    // Řadit podle začátku a pak podle délky (kratší nejdřív) — bez tohodle by pořadí
    // záviselo jen na tom, ve kterém pořadí přijdou v `day.reservations`, a jednou
    // přiřazený track se pak i po uvolnění nižších tracků nikdy nepřepočítá. Kratší
    // rezervace uvolní nízký track dřív, takže ho navazující rezervace může znovu
    // použít, místo aby zbytečně zůstala schovaná za "+N" po celou dobu svého trvání.
    events.sort((a, b) => a.startCol - b.startCol || a.span - b.span)

    const tracks = []
    events.forEach(evt => {
        let ti = tracks.findIndex(track =>
            !track.some(e => e.startCol < evt.startCol + evt.span && e.startCol + e.span > evt.startCol)
        )
        if (ti === -1) { ti = tracks.length; tracks.push([]) }
        tracks[ti].push(evt)
    })
    return tracks
}

// Pro každý den vrátí rezervace ze skrytých (přetečených) multi-day tracků
function overflowMultidayReservationsByDay(week) {
    const tracks = multidayTracksForWeek(week)
    const overflow = Array.from({ length: 7 }, () => [])
    tracks.slice(MAX_MULTIDAY_TRACKS).forEach(track => {
        track.forEach(evt => {
            for (let c = evt.startCol - 1; c < evt.startCol - 1 + evt.span; c++) {
                overflow[c].push(evt.reservation)
            }
        })
    })
    return overflow
}

// Které tracky (0..MAX_MULTIDAY_TRACKS-1) jsou v daný den obsazené viditelným multi-day pruhem
function occupiedTracksByDay(week) {
    const tracks = multidayTracksForWeek(week).slice(0, MAX_MULTIDAY_TRACKS)
    const occupied = Array.from({ length: 7 }, () => new Set())
    tracks.forEach((track, ti) => {
        track.forEach(evt => {
            for (let c = evt.startCol - 1; c < evt.startCol - 1 + evt.span; c++) {
                occupied[c].add(ti)
            }
        })
    })
    return occupied
}

// Pro daný den seřadí jednodenní rezervace podle času a přidělí jim volné sloty
// (ty, co nezabírá multi-day pruh toho dne) — dřívější čas = nižší (viditelnější) slot.
// Co se nevejde, jde do overflow.
function daySingleDayLayout(week, dayIndex) {
    const day = week[dayIndex]
    const slotByReservationId = new Map()
    const overflow = []
    if (!day) return { slotByReservationId, overflow }

    const occupied = occupiedTracksByDay(week)[dayIndex]
    const freeSlots = []
    for (let ti = 0; ti < MAX_MULTIDAY_TRACKS; ti++) {
        if (!occupied.has(ti)) freeSlots.push(ti)
    }

    let reservations = day.reservations.filter(r => r.state !== 'empty' && r.date === day.date)

    // Během tažení jednodenní rezervace: v jejím původním dni ji z výpočtu vyřadíme
    // (ostatní "uhnou" na uvolněné místo) a v aktuálně najížděném cílovém dni ji
    // virtuálně přidáme (ostatní se kolem ní přeuspořádají podle času) — vypadá to,
    // jako by tam reálně byla, ještě než se pustí.
    if (dragging.value && !dragging.value.isMultiday) {
        const dragged = dragging.value.reservation
        if (dragged.date === day.date) {
            reservations = reservations.filter(r => r.id !== dragged.id)
        }
        if (dragging.value.currentDate === day.date) {
            reservations = [...reservations, dragged]
        }
    }

    reservations
        .slice()
        .sort((a, b) => (a.time_start || '99:99:99').localeCompare(b.time_start || '99:99:99'))
        .forEach((r, idx) => {
            if (idx < freeSlots.length) {
                slotByReservationId.set(r.id, freeSlots[idx])
            } else {
                overflow.push(r)
            }
        })

    return { slotByReservationId, overflow }
}

// Vertikální pozice (px) jednodenní rezervace v jejím přiděleném slotu, nebo null když se nevešla
function singleDaySlotTop(week, dayIndex, reservation) {
    const ti = daySingleDayLayout(week, dayIndex).slotByReservationId.get(reservation.id)
    return ti === undefined ? null : ti * ROW_HEIGHT
}

// Součet přetečených multi-day i jednodenních rezervací za den
function overflowCountByDay(week) {
    const multiday = overflowMultidayReservationsByDay(week)
    return week.map((day, dayIndex) => multiday[dayIndex].length + daySingleDayLayout(week, dayIndex).overflow.length)
}

// ─── Overflow popover ────────────────────────────────────────────

const overflowPopover = ref(null)
const overflowList = ref([])

function openOverflow(event, week, dayIndex) {
    const multiday = overflowMultidayReservationsByDay(week)[dayIndex]
    const single = daySingleDayLayout(week, dayIndex).overflow
    overflowList.value = [...multiday, ...single]
    overflowPopover.value?.toggle(event)
}

// Přetažení přímo z popoveru "+N další" — grabOffsetDays 0, protože položka v seznamu
// nemá žádnou konkrétní "chycenou" kolonku (na rozdíl od pruhu v mřížce).
function onOverflowPointerDown(event, reservation) {
    overflowPopover.value?.hide()
    beginPointerDrag(event, reservation, 0)
}

function selectOverflowReservation(event, reservation) {
    overflowPopover.value?.hide()
    emit('toggleEvent', event, reservation)
}

// ─── Helpers ─────────────────────────────────────────────────────

function isToday(day) {
    if (!day || !props.selectedMonth) return false
    const now = new Date()
    return day.day === now.getDate()
        && props.selectedMonth.month() === now.getMonth()
        && props.selectedMonth.year() === now.getFullYear()
}

function onHoverEnter(reservation, event) {
    emit('hoverReservation', { reservation, x: event.clientX, y: event.clientY })
}
function onHoverLeave() {
    emit('hoverReservation', null)
}

// ─── Drag & drop (vlastní mousedown/mousemove/mouseup) ──────────────
// Nativní HTML5 DnD je pro takhle malé prvky napříč prohlížeči nespolehlivé
// (draggable se u malých/absolutně pozicovaných elementů často vůbec nespustí).
// Tohle je plně pod naší kontrolou — funguje jednotně pro jednodenní i multi-day.

const DRAG_THRESHOLD = 4 // px pohybu myší, než se to považuje za tažení (ne klik)
const dragging = ref(null) // { reservation, isMultiday, grabOffsetDays, currentDate } — null dokud se nepřekročí threshold
const ghostEl = ref(null)

// Najde den pod souřadnicemi myši čistě geometricky (podle skutečných pozic
// buněk v okamžiku začátku tažení) — nezávisle na tom, který prvek je zrovna
// "nahoře" na daném pixelu (elementFromPoint/pointer-events se ukázaly
// nespolehlivé u takhle malých, absolutně pozicovaných prvků).
function snapshotDayCells() {
    return Array.from(document.querySelectorAll('[data-cal-date]')).map(el => ({
        date: el.dataset.calDate,
        rect: el.getBoundingClientRect(),
    }))
}

function findDateAt(cells, x, y) {
    const cell = cells.find(c => x >= c.rect.left && x < c.rect.right && y >= c.rect.top && y < c.rect.bottom)
    return cell?.date ?? null
}

function beginPointerDrag(event, reservation, grabOffsetDays) {
    if (event.button !== 0) return
    event.preventDefault() // ať mousedown+pohyb myší nezačne vybírat text na stránce

    const isMultiday = !!(reservation.date_start && reservation.date_end)
    const cells = snapshotDayCells()
    const startX = event.clientX
    const startY = event.clientY

    const positionGhost = (x, y) => {
        if (!ghostEl.value) return
        ghostEl.value.style.left = (x + 14) + 'px'
        ghostEl.value.style.top = (y + 10) + 'px'
    }

    const onMove = (e) => {
        if (!dragging.value) {
            if (Math.hypot(e.clientX - startX, e.clientY - startY) < DRAG_THRESHOLD) return
            // Přiřazením se z reservation/isMultiday/grabOffsetDays stane reaktivní
            // objekt — od teď na něj musíme sahat přes dragging.value, ne přes
            // uzavřené proměnné, jinak by šablona (duch, zvýraznění cíle) nereagovala.
            dragging.value = { reservation, isMultiday, grabOffsetDays, currentDate: null }
            document.body.style.cursor = 'grabbing'
            document.body.style.userSelect = 'none'
        }
        dragging.value.currentDate = findDateAt(cells, e.clientX, e.clientY)
        positionGhost(e.clientX, e.clientY)
    }

    const onUp = () => {
        window.removeEventListener('mousemove', onMove)
        window.removeEventListener('mouseup', onUp)
        document.body.style.cursor = ''
        document.body.style.userSelect = ''
        const finished = dragging.value
        dragging.value = null
        if (finished?.currentDate) finishDrop(finished)
    }

    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
}

function onChipPointerDown(event, reservation) {
    beginPointerDrag(event, reservation, 0)
}

// Multi-day pruh může jít táhnout odkudkoliv v jeho rozsahu — grabOffsetDays si
// pamatuje, kolikátý den od date_start uživatel chytil, aby po puštění zůstal
// posun konzistentní s tím, kde myš pruh uchopila.
function onPillPointerDown(event, trackEvt, week) {
    const rect = event.currentTarget.getBoundingClientRect()
    const frac = Math.min(0.999, Math.max(0, (event.clientX - rect.left) / rect.width))
    const colOffset = Math.floor(frac * trackEvt.span)
    const grabDay = week[trackEvt.startCol - 1 + colOffset]
    const grabOffsetDays = grabDay
        ? moment(grabDay.date).diff(moment(trackEvt.reservation.date_start), 'days')
        : 0
    beginPointerDrag(event, trackEvt.reservation, grabOffsetDays)
}

function finishDrop({ reservation, isMultiday, grabOffsetDays, currentDate }) {
    if (isMultiday) {
        const newDateStart = moment(currentDate).subtract(grabOffsetDays, 'days').format('YYYY-MM-DD')
        if (newDateStart === reservation.date_start) return
        const duration = moment(reservation.date_end).diff(moment(reservation.date_start), 'days')
        const newDateEnd = moment(newDateStart).add(duration, 'days').format('YYYY-MM-DD')
        emit('captureReservationChange', { reservation, newDateStart, newDateEnd })
    } else {
        if (currentDate === reservation.date) return
        emit('captureReservationChange', { reservation, newDate: currentDate })
    }
}
</script>

<template>
    <div class="max-lg:hidden border-t border-neutral-100 dark:border-neutral-800 flex-1 min-h-0 flex flex-col overflow-hidden bg-white dark:bg-neutral-900">
        <div v-if="days.length" class="flex-1 min-h-0 flex flex-col overflow-hidden">
            <!-- Řádek hlaviček (Po–Ne) — jen jednou, nad prvním týdnem -->
            <div class="grid grid-cols-7 flex-shrink-0 bg-white dark:bg-neutral-900 border-b border-neutral-100 dark:border-neutral-800">
                <div
                    v-for="(name, i) in DAY_NAMES"
                    :key="name"
                    class="py-2 px-3 text-[11px] font-semibold uppercase tracking-widest text-neutral-400 dark:text-neutral-500"
                    :class="{ 'text-neutral-300 dark:text-neutral-600': i >= 5, 'border-l border-neutral-100 dark:border-neutral-800': i > 0 }"
                >
                    {{ name }}
                </div>
            </div>

            <!-- Týdny -->
            <div
                v-for="(week, weekIndex) in weeks"
                :key="weekIndex"
                class="relative flex-1 min-h-0 flex flex-col border-b border-neutral-100 dark:border-neutral-800 last:border-b-0"
            >
                <!-- Svislé oddělovací čáry mezi dny — nezávislé na počtu multi-day tracků,
                     jinak by v místě tracků vznikala "díra" v čárách. -->
                <div class="absolute inset-0 z-0 grid grid-cols-7 pointer-events-none">
                    <div
                        v-for="dayIndex in 7"
                        :key="dayIndex"
                        :class="{
                            'border-l border-neutral-100 dark:border-neutral-800': dayIndex > 1,
                            'bg-neutral-50/60 dark:bg-neutral-800/40': dayIndex > 5,
                        }"
                    ></div>
                </div>

                <!-- Číslice dnů -->
                <div class="relative z-10 grid grid-cols-7 flex-shrink-0">
                    <div
                        v-for="(day, dayIndex) in week"
                        :key="dayIndex"
                        class="px-2 pt-1.5 pb-0.5 flex items-start"
                    >
                        <div
                            v-if="day"
                            class="w-6 h-6 flex items-center justify-center rounded-full text-[12px] font-medium leading-none"
                            :class="isToday(day)
                                ? 'bg-blue-600 text-white font-semibold'
                                : 'text-neutral-500 dark:text-neutral-400'"
                        >
                            {{ day.day }}
                        </div>
                    </div>
                </div>

                <!-- Sjednocená mřížka slotů: multi-day pruhy i jednodenní rezervace sdílí stejné řádky,
                     jednodenní rezervace zaplní volné sloty (podle času) místo aby byly vždy pod pruhy. -->
                <div class="relative flex-shrink-0" :style="{ height: (MAX_MULTIDAY_TRACKS * ROW_HEIGHT) + 'px' }">
                    <!-- Multi-day pruhy — samotný řádek nemá pointer-events (jinak by jeho prázdné
                         místo, mimo skutečný pruh, blokovalo kliky na jednodenní rezervace pod ním),
                         jen konkrétní .multiday-pill je klikatelný/přetahovatelný. -->
                    <div
                        v-for="(track, ti) in multidayTracksForWeek(week).slice(0, MAX_MULTIDAY_TRACKS)"
                        :key="ti"
                        class="multiday-track relative z-20"
                        :style="{ height: ROW_HEIGHT + 'px' }"
                    >
                        <div
                            v-for="evt in track"
                            :key="evt.reservation.id"
                            class="multiday-pill"
                            :class="{ 'dragging-active': dragging?.reservation?.id === evt.reservation.id }"
                            :style="{
                                gridColumn: `${evt.startCol} / span ${evt.span}`,
                                ...multidayPillStyle(evt.reservation),
                            }"
                            @click="emit('toggleEvent', $event, evt.reservation)"
                            @mouseenter="onHoverEnter(evt.reservation, $event)"
                            @mouseleave="onHoverLeave"
                            @mousedown="onPillPointerDown($event, evt, week)"
                        >
                            {{ evt.reservation.name }}
                        </div>
                    </div>

                    <!-- Jednodenní rezervace — umístěné do volných slotů; každá buňka dne je zároveň drop zóna -->
                    <div class="absolute inset-0 z-10 grid grid-cols-7">
                        <div
                            v-for="(day, dayIndex) in week"
                            :key="dayIndex"
                            class="relative min-w-0 h-full rounded transition-colors"
                            :class="{ 'bg-blue-100/60 dark:bg-blue-500/20': dragging && dragging.currentDate === day?.date }"
                            :data-cal-date="day?.date"
                        >
                            <template v-if="day">
                                <template
                                    v-for="reservation in day.reservations.filter(r => r.state !== 'empty' && r.date === day.date)"
                                    :key="reservation.id"
                                >
                                    <!-- v-if místo CSS 'hidden' třídy — ta měla stejnou specificitu jako
                                         scoped .event-chip{display:flex} a v bundlu prohrávala, takže
                                         "skryté" (přeskočené) rezervace zůstávaly vidět na top:0 a
                                         překrývaly multi-day pruhy. -->
                                    <div
                                        v-if="singleDaySlotTop(week, dayIndex, reservation) !== null"
                                        class="event-chip absolute left-[3px] right-[3px]"
                                        :class="{ 'dragging-active': dragging?.reservation?.id === reservation.id }"
                                        :style="{
                                            top: singleDaySlotTop(week, dayIndex, reservation) + 'px',
                                            ...singleDayChipStyle(reservation),
                                        }"
                                        :data-reservation-id="reservation.id"
                                        @click="emit('toggleEvent', $event, reservation)"
                                        @mouseenter="onHoverEnter(reservation, $event)"
                                        @mouseleave="onHoverLeave"
                                        @mousedown="onChipPointerDown($event, reservation)"
                                    >
                                        <span v-if="reservation.time_start" class="event-chip-time">
                                            {{ reservation.time_start.substring(0, 5) }}
                                        </span>
                                        <span class="event-chip-name">{{ reservation.name }}</span>
                                    </div>
                                </template>

                                <!-- Náhled tažené jednodenní rezervace na cílovém místě, ještě před puštěním -->
                                <div
                                    v-if="dragging && !dragging.isMultiday && dragging.currentDate === day.date"
                                    class="event-chip drag-preview absolute left-[3px] right-[3px]"
                                    :style="{
                                        top: (daySingleDayLayout(week, dayIndex).slotByReservationId.get(dragging.reservation.id) ?? 0) * ROW_HEIGHT + 'px',
                                        ...singleDayChipStyle(dragging.reservation),
                                    }"
                                >
                                    <span v-if="dragging.reservation.time_start" class="event-chip-time">
                                        {{ dragging.reservation.time_start.substring(0, 5) }}
                                    </span>
                                    <span class="event-chip-name">{{ dragging.reservation.name }}</span>
                                </div>
                            </template>
                        </div>
                    </div>
                </div>

                <!-- Overflow indikátor (multi-day i jednodenní dohromady) -->
                <div class="flex-1 min-h-0 grid grid-cols-7 overflow-hidden">
                    <div
                        v-for="(day, dayIndex) in week"
                        :key="dayIndex"
                        class="relative z-10 min-w-0 px-[3px]"
                        :data-cal-date="day?.date"
                    >
                        <div
                            v-if="overflowCountByDay(week)[dayIndex] > 0"
                            class="text-[11px] text-neutral-400 dark:text-neutral-500 font-medium px-[4px] cursor-pointer hover:text-neutral-700 dark:hover:text-neutral-300"
                            @click="openOverflow($event, week, dayIndex)"
                        >
                            + {{ $t('common.n_more', { count: overflowCountByDay(week)[dayIndex] }) }}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <Popover ref="overflowPopover" class="!rounded-xl">
        <div class="flex flex-col gap-1 p-1 min-w-44 max-w-64">
            <div
                v-for="reservation in overflowList"
                :key="reservation.id"
                class="px-2.5 py-1.5 rounded-lg text-[12px] font-medium cursor-pointer truncate transition-[filter] hover:brightness-95"
                :style="multidayPillStyle(reservation)"
                @click="selectOverflowReservation($event, reservation)"
                @mousedown="onOverflowPointerDown($event, reservation)"
            >
                {{ reservation.name }}
            </div>
        </div>
    </Popover>

    <!-- Duch přetahované rezervace, sleduje kurzor — vypadá přesně jako reálná rezervace -->
    <div
        v-if="dragging"
        ref="ghostEl"
        class="drag-ghost"
        :style="dragging.isMultiday ? multidayPillStyle(dragging.reservation) : singleDayChipStyle(dragging.reservation)"
    >
        <template v-if="!dragging.isMultiday && dragging.reservation.time_start">
            <span class="event-chip-time">{{ dragging.reservation.time_start.substring(0, 5) }}</span>
        </template>
        <span>{{ dragging.reservation.name }}</span>
    </div>
</template>

<style scoped lang="scss">
/* ── Multi-day pruhy ── */
.multiday-track {
    display: grid;
    grid-template-columns: repeat(7, 1fr);
    flex-shrink: 0;
    padding: 0.5px 2px;
    gap: 0;
    /* Řádek jako grid kontejner přirozeně zabírá celou šířku (všech 7 dní), i když
       samotný pruh uvnitř sahá jen přes pár sloupců — bez tohodle by prázdné místo
       řádku (mimo pruh) blokovalo kliky/drag na cokoliv pod ním (viz .multiday-pill). */
    pointer-events: none;
}

.multiday-pill {
    min-width: 0;
    padding: 1px 6px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: 500;
    cursor: pointer;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    display: block;
    margin: 0.5px 2px;
    height: 15px;
    line-height: 13px;
    transition: filter .1s;
    pointer-events: auto;

    &:hover { filter: brightness(.92); }
}

.dragging-active {
    opacity: .45;
    outline: 2px dashed rgba(0, 0, 0, .35);
    outline-offset: -1px;
    cursor: grabbing !important;
}

.drag-ghost {
    position: fixed;
    top: 0;
    left: 0;
    z-index: 9999;
    pointer-events: none;
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 1px 6px;
    border-radius: 4px;
    height: 15px;
    line-height: 13px;
    font-size: 10px;
    font-weight: 500;
    max-width: 220px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    box-shadow: 0 4px 14px rgba(0, 0, 0, .25);
    transform: rotate(-1deg);
}

.drag-preview {
    z-index: 5;
    pointer-events: none;
    opacity: .85;
    outline: 1.5px dashed rgba(255, 255, 255, .8);
    outline-offset: -2px;
}

/* ── Buňky jednodenních rezervací ── */
.day-cell-removed { display: none; } /* placeholder */

.day-cell {
    min-width: 0;
    overflow: hidden;
    background: white;
}

/* ── Event chip (jednodenní) ── */
.event-chip {
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 0 6px;
    border-radius: 4px;
    height: 15px;
    font-size: 10px;
    font-weight: 500;
    cursor: pointer;
    overflow: hidden;
    white-space: nowrap;
    transition: filter .1s, top .15s ease;
    user-select: none;

    &:hover { filter: brightness(.92); }
}

.event-chip-time {
    font-size: 9px;
    font-weight: 600;
    opacity: .75;
    flex-shrink: 0;
    letter-spacing: .02em;
    font-variant-numeric: tabular-nums;
}

.event-chip-name {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
</style>
